import { useEffect, useState, useCallback } from "react";
import DashboardShell from "@/components/DashboardShell";
import api, { formatApiError, downloadInvoicePdf, downloadPdf, fileUrl } from "@/lib/api";
import OrderMiniMap from "@/components/OrderMiniMap";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  LayoutDashboard, Package, Users, Tag, Truck, FileText, CreditCard, Settings as SettingsIcon,
  Plus, Trash2, Check, AlertTriangle, Download, Pencil, Repeat, Pause, Play, Mail, MessageSquare, QrCode, Scale, History, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const money = (n) => "£" + Number(n || 0).toFixed(2);
const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "orders", label: "Orders", icon: Package },
  { key: "recurring", label: "Recurring", icon: Repeat },
  { key: "clients", label: "Clients", icon: Users },
  { key: "pricing", label: "Price list", icon: Tag },
  { key: "drivers", label: "Drivers & jobs", icon: Truck },
  { key: "invoices", label: "Invoices", icon: FileText },
  { key: "subscription", label: "Subscription", icon: CreditCard },
  { key: "account", label: "Settings", icon: SettingsIcon },
];
const STATUS_COLORS = {
  new: "bg-slate-100 text-slate-700", collected: "bg-blue-100 text-blue-700",
  processing: "bg-amber-100 text-amber-700", ready: "bg-teal-100 text-teal-700",
  out_for_delivery: "bg-indigo-100 text-indigo-700", completed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};


function Subscription({ me }) {
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)); }, []);
  const sub = me?.subscription_state;

  const subscribe = async (plan) => {
    const lookup = cycle === "monthly" ? plan.lookup_key_monthly : plan.lookup_key_yearly;
    setBusy(lookup);
    try {
      const { data } = await api.post("/business/subscription/checkout", { lookup_key: lookup, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(""); }
  };

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold text-slate-900">Subscription</h1>
      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid="subscription-status">
        <p className="text-sm text-slate-500">Current status</p>
        <div className="mt-1 flex items-center gap-3">
          <Badge className={`border-0 ${sub?.active ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{sub?.status?.replace("_", " ")}</Badge>
          {me?.plan && <span className="font-medium">{me.plan.name}</span>}
          {sub?.trial_days_left != null && <span className="text-sm text-slate-500">{sub.trial_days_left} trial days left</span>}
          {sub?.current_period_end && <span className="text-sm text-slate-500">Renews {sub.current_period_end.slice(0,10)}</span>}
        </div>
      </div>

      <div className="mt-6 flex items-center gap-2">
        <button onClick={() => setCycle("monthly")} className={`rounded-full px-4 py-1.5 text-sm font-medium ${cycle === "monthly" ? "bg-brand text-white" : "bg-slate-100 text-slate-600"}`} data-testid="cycle-monthly">Monthly</button>
        <button onClick={() => setCycle("yearly")} className={`rounded-full px-4 py-1.5 text-sm font-medium ${cycle === "yearly" ? "bg-brand text-white" : "bg-slate-100 text-slate-600"}`} data-testid="cycle-yearly">Yearly (save ~17%)</button>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        {plans.map((p) => (
          <div key={p.id} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`sub-plan-${p.name.toLowerCase()}`}>
            <h3 className="font-heading text-lg font-bold">{p.name}</h3>
            <p className="text-sm text-slate-500">{p.description}</p>
            <p className="mt-4 font-heading text-3xl font-extrabold">{money(cycle === "monthly" ? p.price_monthly : p.price_yearly)}<span className="text-base font-medium text-slate-400">/{cycle === "monthly" ? "mo" : "yr"}</span></p>
            <ul className="mt-4 space-y-2">{(p.features || []).map((f, i) => <li key={i} className="flex items-start gap-2 text-sm"><Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" /> {f}</li>)}</ul>
            <Button onClick={() => subscribe(p)} disabled={busy} className="mt-5 w-full rounded-full bg-brand hover:bg-brand-hover" data-testid={`subscribe-${p.name.toLowerCase()}`}>{busy ? "Redirecting…" : "Subscribe"}</Button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------- Account -------------------------------- */

export default Subscription;
